/**
 * Permissions-only UserSelector (tech debt).
 *
 * Do not use this component outside the permissions module. It exists only because
 * VisibilitySection needs to support external and partner affiliations, which
 * EmployeeSelectorV2 does not support yet. This is a duplicate of the removed core
 * UserSelector, kept here as tech debt until we can migrate to GraphQL.
 *
 * For any other feature, use EmployeeSelectorV2 from modules/employees/components/EmployeeSelectorV2.
 */
import * as React from 'react'

import {
  Box,
  CornerIndicator,
  SearchField,
  Text,
  Touchable,
} from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import sortBy from 'lodash/sortBy'

import { observer } from 'mobx-react'

import BoxList from 'modules/core/components/BoxList'
import EmployeeMultiFilters from 'modules/core/components/EmployeeMultiFilters'
import useEmployeeMultiFilters, {
  FilterOptions,
} from 'modules/core/components/EmployeeMultiFilters/hooks/useEmployeeMultiFilters'
import type { FilterOption } from 'modules/core/components/Filters/computeOptions'
import type { FilterOption as MultiFilterOption } from 'modules/core/components/MultiFilters/types'
import type { Size } from 'modules/core/components/ScrollableList'
import ScrollableList from 'modules/core/components/ScrollableList'
import { Attachment } from 'modules/core/components/TetherComponent/types'
import createComputed from 'modules/core/hooks/createComputed'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import useFilters from 'modules/core/hooks/useFilters'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'
import type User from 'modules/core/store/models/User'

import Loading from 'modules/layout/components/Page/Loading'

import Controls from './Controls'
import List from './List'
import { GetUserExtraInformationFn } from './List/Item'
import filterFields, { Fields } from './_filters/filterFields'
import getFilterValue from './_filters/getFilterValue'

const useSearchedUsers = createComputed(
  (filteredUsers: User[], search: string, autoSort: boolean): User[] => {
    const usersToDisplay = filteredUsers.filter((user) => user.matchesFullName(search))

    if (!autoSort) return usersToDisplay

    return sortBy(usersToDisplay, (user) =>
      getFullDisplayName({
        firstName: user.attributes.first_name,
        lastName: user.attributes.last_name,
      })
    )
  }
)

const trackUsage = () => {
  track(trackingEvents.employee.employeeSelectorFilters)
}

interface Props {
  users: User[]
  showAll?: boolean
  onCleanFilter?: () => void
  onSelect: (id: number | null | undefined) => void
  onSelectMultiple?: ((ids: number[]) => void) | null
  options?: Record<string, FilterOption[]>
  disabled?: boolean
  mandatory?: boolean
  attachmentPosition?: Attachment
  selectedIds: number[]
  autoSort?: boolean
  suggestedUsers?: number[]
  confirmedUsers?: number[]
  size?: Size
  setDisplayedUsers?: ((accessIds: number[]) => void) | null | undefined
  setChildPopoverOpen?: (childPopoverOpen: boolean) => void
  hideFields?: Fields[]
  showDefaultFilters?: boolean
  disabledUsers?: number[]
  onGetUserExtraInformation?: GetUserExtraInformationFn
  applyDefaultFilters?: boolean
  skipDefaultFilters?: FilterOptions
  filterKey?: string
  showControls?: boolean
  virtualized?: boolean
}

function UserSelector({
  users,
  showAll = false,
  onCleanFilter,
  onSelect,
  onSelectMultiple,
  selectedIds,
  disabled = false,
  mandatory = false,
  attachmentPosition = 'bottom center',
  autoSort = true,
  suggestedUsers,
  confirmedUsers,
  size,
  setDisplayedUsers,
  setChildPopoverOpen,
  hideFields = [Fields.UserType],
  showDefaultFilters = false,
  disabledUsers,
  onGetUserExtraInformation,
  applyDefaultFilters = false,
  skipDefaultFilters = {},
  filterKey = LOCAL_STORAGE_KEYS.employeeSelectorFilters,
  showControls = true,
  virtualized = true,
}: Props) {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const onSelectUser = (user: User) => () => !disabled && onSelect(user.attributes.id)

  const {
    filteredModels: filteredUsers,
    logicOperator,
    filters,
    onApplyFilters,
    onCancel,
    onRemoveFilter,
    onAddFilter,
  } = useEmployeeMultiFilters(getFilterValue, filterKey, skipDefaultFilters)

  const { loading: loadingFilters, users: filteredUsersFromSelect } = useFilters(users)

  React.useEffect(() => {
    if (!showDefaultFilters) onApplyFilters(users)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const applyMultiFilters = filters.length

  let filteredUsersToUse
  if (showAll) {
    filteredUsersToUse = users
  } else {
    filteredUsersToUse =
      applyMultiFilters || applyDefaultFilters ? filteredUsers : filteredUsersFromSelect
  }

  const searchedUsers = useSearchedUsers(filteredUsersToUse, debouncedSearch, autoSort).get()

  React.useEffect(() => {
    setDisplayedUsers?.(searchedUsers.map((e) => e.attributes.id))
  }, [searchedUsers]) // eslint-disable-line react-hooks/exhaustive-deps

  const [fields, setFields] = React.useState<readonly MultiFilterOption[]>([])

  const { loading } = useAsyncData(async () => {
    const fieldsResult = await filterFields(hideFields)
    setFields(fieldsResult)
  }, [])

  if (loading || loadingFilters) {
    return <Loading />
  }

  return (
    <Box position="relative">
      {mandatory && (
        <CornerIndicator
          attachmentPosition={attachmentPosition}
          errors={false}
          missing={selectedIds.length == 0}
          missingLabel={i18n.t('common.missing_field')}
        />
      )}
      <BoxList>
        <Box width="full" flexDirection="row" background="grey200" gap="s8" padding="s8">
          <Box width="s140" onClick={() => trackUsage()}>
            <EmployeeMultiFilters
              label={i18n.t('filters.multi_filters.title')}
              fields={fields}
              filters={filters}
              logicOperator={logicOperator}
              onAddFilter={onAddFilter}
              onRemoveFilter={onRemoveFilter}
              onApplyFilters={(defaultFilter) => onApplyFilters(users, defaultFilter)}
              onCancel={onCancel}
              setChildPopoverOpen={setChildPopoverOpen}
              showDefaultFilters={showDefaultFilters}
              filterKey={filterKey}
            />
          </Box>
          <Box flexGrow>
            <SearchField
              variant="big"
              search={search}
              onSearch={onSearch}
              placeholder={i18n.t('common.search')}
            />
          </Box>
        </Box>
        {showControls && (
          <Controls
            users={searchedUsers}
            disabled={disabled}
            selectedIds={selectedIds}
            onSelectMultiple={onSelectMultiple}
            disabledIds={disabledUsers}
          />
        )}
        {virtualized ? (
          <List
            isMultiSelect={!!onSelectMultiple}
            onSelectUser={onSelectUser}
            selectedIds={selectedIds}
            users={searchedUsers}
            suggestedUsers={suggestedUsers}
            confirmedUsers={confirmedUsers}
            disabledUsers={disabledUsers}
            onGetUserExtraInformation={onGetUserExtraInformation}
            size={size}
            virtualized
          />
        ) : (
          <ScrollableList size={size}>
            <List
              isMultiSelect={!!onSelectMultiple}
              onSelectUser={onSelectUser}
              selectedIds={selectedIds}
              users={searchedUsers}
              suggestedUsers={suggestedUsers}
              confirmedUsers={confirmedUsers}
              disabledUsers={disabledUsers}
              onGetUserExtraInformation={onGetUserExtraInformation}
            />
          </ScrollableList>
        )}

        {onCleanFilter && (
          <Box width="full" padding="s12" alignItems="center" justifyContent="center">
            <Touchable onClick={onCleanFilter}>
              <Text size="200" color="radicalRed1200">
                {i18n.t('filters.clean_filter')}
              </Text>
            </Touchable>
          </Box>
        )}
      </BoxList>
    </Box>
  )
}

export default observer(UserSelector)
